import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { 
  BankAccount, 
  PensionConsent, 
  PensionSetupValidation,
  PensionEnrollmentRequest,
  PensionEnrollmentResponse,
  PensionOptOutRequest,
  PensionOptOutResponse
} from "@/types/pensions";

class PensionAPIService {
  // Bank Account Management
  static async saveBankAccount(data: BankAccount): Promise<BankAccount> {
    return ApiService.request<BankAccount>(
      EndpointKeys.SAVE_PENSION_BANK_ACCOUNT,
      {
        method: "POST",
        body: data,
      }
    );
  }

  static async getBankAccount(): Promise<BankAccount | null> {
    const response = await ApiService.request<{ bankAccount: BankAccount | null }>(
      EndpointKeys.GET_PENSION_BANK_ACCOUNT,
      {
        method: "GET",
      }
    );
    return response.bankAccount;
  }

  // Consent Management
  static async recordConsent(data: PensionConsent): Promise<PensionConsent> {
    return ApiService.request<PensionConsent>(
      EndpointKeys.RECORD_PENSION_CONSENT,
      {
        method: "POST",
        body: data,
      }
    );
  }

  static async getConsent(): Promise<PensionConsent | null> {
    const response = await ApiService.request<{ consent: PensionConsent | null }>(
      EndpointKeys.GET_PENSION_CONSENT,
      {
        method: "GET",
      }
    );
    return response.consent;
  }

  // Validation
  static async validatePensionSetup(): Promise<PensionSetupValidation> {
    return ApiService.request<PensionSetupValidation>(
      EndpointKeys.VALIDATE_PENSION_SETUP,
      {
        method: "GET",
      }
    );
  }

  static async validatePensionPostponement(): Promise<PensionSetupValidation> {
    return ApiService.request<PensionSetupValidation>(
      EndpointKeys.VALIDATE_PENSION_POSTPONEMENT,
      {
        method: "GET",
      }
    );
  }

  // Get Terms PDF URL
  static getPensionTermsPdfUrl(): string {
    // This returns the full URL for the PDF endpoint
    // For now, return a placeholder URL - in production this would be a real PDF URL
    return "/api/employees/benefits/pension-setup/terms-pdf/";
  }

  // Quick Actions
  static async setupPension(
    employeeId: number,
    setupData: {
      bankName: string;
      sortCode: string;
      accountNumber: string;
      hireloConsent: boolean;
      nestTerms: boolean;
      employerContributionPercent?: number;
      employeeContributionPercent?: number;
    }
  ): Promise<{ status: string }> {
    return ApiService.request(EndpointKeys.PENSION_SETUP, {
      method: "POST",
      urlParams: { employeeId: employeeId.toString() },
      body: setupData,
    });
  }

  static async postponePension(
    employeeId: number,
    consentData: {
      hireloConsent: boolean;
      nestTerms: boolean;
    }
  ): Promise<{ success: boolean; status: string; error?: string }> {
    return ApiService.request(EndpointKeys.PENSION_POSTPONE, {
      method: "POST", 
      urlParams: { employeeId: employeeId.toString() },
      body: consentData,
    });
  }

  static async processCatchUp(employeeId: number): Promise<{ status: string; catchUpAmount: number }> {
    return ApiService.request(EndpointKeys.PENSION_CATCH_UP, {
      method: "POST",
      urlParams: { employeeId: employeeId.toString() },
    });
  }

  static async setEmployerContribution(employeeId: number, percent: number): Promise<{ status: string }> {
    return ApiService.request(EndpointKeys.PENSION_CHANGE_EMPLOYER_CONTRIBUTION, {
      method: "POST",
      urlParams: { employeeId: employeeId.toString() },
      body: { percent },
    });
  }

  static async employeeEnrollNow(
    employeeId: number,
    request: PensionEnrollmentRequest
  ): Promise<PensionEnrollmentResponse> {
    return ApiService.request<PensionEnrollmentResponse>(EndpointKeys.PENSION_EMPLOYEE_ENROLL, {
      method: "POST",
      urlParams: { employeeId: employeeId.toString() },
      body: request,
    });
  }

  static async employeeOptOut(
    employeeId: number, 
    request: PensionOptOutRequest
  ): Promise<PensionOptOutResponse> {
    return ApiService.request<PensionOptOutResponse>(EndpointKeys.PENSION_EMPLOYEE_OPT_OUT, {
      method: "POST",
      urlParams: { employeeId: employeeId.toString() },
      body: request,
    });
  }

  static async employeeReenroll(
    employeeId: number,
    setupData: {
      bankName: string;
      sortCode: string;
      accountNumber: string;
      hireloConsent: boolean;
      nestTerms: boolean;
      employerContributionPercent?: number;
      employeeContributionPercent?: number;
    }
  ): Promise<{ status: string }> {
    return ApiService.request(EndpointKeys.PENSION_EMPLOYEE_REENROLL, {
      method: "POST",
      urlParams: { employeeId: employeeId.toString() },
      body: setupData,
    });
  }

  static async setEmployeeContribution(employeeId: number, percent: number): Promise<{ status: string }> {
    return ApiService.request(EndpointKeys.PENSION_EMPLOYEE_CHANGE_CONTRIBUTION, {
      method: "POST",
      urlParams: { employeeId: employeeId.toString() },
      body: { percent },
    });
  }

  static async getPensionStatus(employeeId: number): Promise<{
    isEnrolled: boolean;
    isOptedOut: boolean;
    isPostponed: boolean;
    postponeStartDate: string | null;
    postponeEndDate: string | null;
    autoEnrollDue: string | null;
    canOptOut: boolean;
    canOptIn: boolean;
    hasDeferredHistory: boolean;
    requiresEmployerSetup: boolean;
    lastAction: string | null;
    employerContributionPercent: number | null;
    employeeContributionPercent: number | null;
  }> {
    return ApiService.request(EndpointKeys.PENSION_STATUS, {
      method: "GET",
      urlParams: { employeeId: employeeId.toString() },
    });
  }

  static async getPostponementStatus(employeeId: number): Promise<{
    isPostponed: boolean;
    postponementStartDate?: string;
    postponementEndDate?: string;
    catchUpRequired?: boolean;
    catchUpAmount?: number;
    isExpired?: boolean;
    lastAction?: string;
  }> {
    return ApiService.request(EndpointKeys.PENSION_POSTPONEMENT_STATUS, {
      method: "GET",
      urlParams: { employeeId: employeeId.toString() },
    });
  }
}

export default PensionAPIService;